// Watch out! We should only import the types here so that the files are not imported during runtime on the client
// They should not be visible to the client and would often throw an error (e.g. when importing some server-only libraries)

import { generateSignatureHeaders, isSignedRoute } from "./api-signature";
import { notNull } from "./helpers";
import { waitForFreshApiSession } from "./supabase/apiSessionCheck";
import { getTraceHeaders } from "./tracing/client";
import type { RemoveExcessProperties } from "./types";
import type { GrantAccessRoute } from "@/app/api/access/grant/route";
import type { SetLinkSharingRoute } from "@/app/api/access/link/route";
import type { ListAccessRoute } from "@/app/api/access/list/route";
import type { SetRestrictionRoute } from "@/app/api/access/restriction/route";
import type { RevokeAccessRoute } from "@/app/api/access/revoke/route";
import type { GetBillingEntitiesRoute } from "@/app/api/admin/billing-entities/get";
import type { GetWalletsRoute } from "@/app/api/admin/billing-entities/wallets/get";
import type { GetCreditUsageOverrideRoute } from "@/app/api/admin/credits/overrides/get";
import type { PatchCreditUsageOverridesRoute } from "@/app/api/admin/credits/overrides/patch";
import type { UpdateCreditsRoute } from "@/app/api/admin/credits/update/route";
import type { UpdateBlocklistRoute } from "@/app/api/admin/identity/blocklist/route";
import type { ResolveIdentityRoute } from "@/app/api/admin/identity/resolve/route";
import type { ListWorkspacesWithoutSupervisorAccessRoute } from "@/app/api/admin/internal-supervisor-memberships/get";
import type { ListWorkspaceSurveysRoute } from "@/app/api/admin/internal-supervisor-memberships/org-surveys/get";
import type { RequestInternalSupervisorMembershipRoute } from "@/app/api/admin/internal-supervisor-memberships/post";
import type { WebhookPortalRoute } from "@/app/api/admin/orgs/[orgId]/webhook-portal/route";
import type { PatchTargetGroupRoute } from "@/app/api/admin/recruitment/target_group/patch";
import type { UserInterviewParticipantGetRoute } from "@/app/api/admin/recruitment/user_interviews/participant/get";
import type { UserInterviewParticipantPatchRoute } from "@/app/api/admin/recruitment/user_interviews/participant/patch";
import type { PingRenderWorkflowRoute } from "@/app/api/admin/render-workflows/ping/route";
import type { OverrideQualityScoreRoute } from "@/app/api/admin/responses/quality-score/route";
import type { AdminUpdateRecruitedResponseRoute } from "@/app/api/admin/responses/recruited-responses/route";
import type { RerunPostProcessingRoute } from "@/app/api/admin/responses/rerun-post-processing/route";
import type { GetStudiesRoute } from "@/app/api/admin/studies/get";
import type { WebhookTestSendRoute } from "@/app/api/admin/webhooks/test-send/route";
import type {
  ChangeWorkspaceOrganizationRoute,
  GetWorkspaceOrganizationRoute,
} from "@/app/api/admin/workspace-organization/route";
import type { AgreementVersionReadRoute } from "@/app/api/agreements/version/[versionId]/route";
import type { AgreementVersionCreateRoute } from "@/app/api/agreements/version/route";
import type { GetAnalysisChatsRoute } from "@/app/api/analysis-chat/get_chats/route";
import type { GetThemesRoute } from "@/app/api/analysis-chat/get_themes/route";
import type { ArtifactSharingRoute } from "@/app/api/analysis-chat/memo/sharing/route";
import type { GetArtifactsForSurveyRoute } from "@/app/api/analysis-chat/shareable-artifacts/route";
import type {
  DeleteAnalysisChatRoute,
  UpdateAnalysisChatRoute,
} from "@/app/api/analysis-chat/update/route";
import type { AddReportItemRoute } from "@/app/api/analysis/add-report-item/route";
import type { LoadAnalysisDataRoute } from "@/app/api/analysis/agentic/analysisData/route";
import type { GetArtifactRoute } from "@/app/api/analysis/agentic/data/artifact/route";
import type { ClassificationRecomputeStatusRoute } from "@/app/api/analysis/agentic/data/classification/recompute-status/route";
import type { UpdateClassificationRoute } from "@/app/api/analysis/agentic/data/classification/update/route";
import type { GetColumnsRoute } from "@/app/api/analysis/agentic/data/column/route";
import type { ComputeChartRoute } from "@/app/api/analysis/agentic/data/compute-chart/route";
import type { ExportPreviewChartImageRoute } from "@/app/api/analysis/agentic/data/export-preview-chart-image/route";
import type { GetReelRoute } from "@/app/api/analysis/agentic/data/reel/route";
import type { GetAgenticReportStatusRoute } from "@/app/api/analysis/agentic/data/report-status/route";
import type { GetSegmentDefinitionsRoute } from "@/app/api/analysis/agentic/data/segment-definitions/route";
import type { GetAgenticAnalysisStateRoute } from "@/app/api/analysis/agentic/data/state/route";
import type { GetStaticAnalysisDataRoute } from "@/app/api/analysis/agentic/data/static/route";
import type { GenerateAgenticAnalysisRoute } from "@/app/api/analysis/agentic/generate/route";
import type { PresentationDetailsRoute } from "@/app/api/analysis/agentic/presentation/details/route";
import type { ExportToDriveRoute } from "@/app/api/analysis/agentic/presentation/export-to-drive/route";
import type { GeneratePresentationV2Route } from "@/app/api/analysis/agentic/presentation/generate/route";
import type { PollPresentationV2Route } from "@/app/api/analysis/agentic/presentation/poll/route";
import type { CreateAgentReportRoute } from "@/app/api/analysis/agentic/report/create/route";
import type { DeleteAgentReportRoute } from "@/app/api/analysis/agentic/report/delete/route";
import type { DuplicateAgentReportRoute } from "@/app/api/analysis/agentic/report/duplicate/route";
import type { CreateReportFileArtifactRoute } from "@/app/api/analysis/agentic/report/edit/upload/create-artifact/route";
import type { ListInProgressReportGenerationRunsRoute } from "@/app/api/analysis/agentic/report/generate/in-progress/route";
import type { GenerateAgentReportRoute } from "@/app/api/analysis/agentic/report/generate/route";
import type { ListAgentReportsRoute } from "@/app/api/analysis/agentic/report/list/route";
import type { SetMainReportRoute } from "@/app/api/analysis/agentic/report/set-main/route";
import type { UpdateAgentReportRoute } from "@/app/api/analysis/agentic/report/update/route";
import type { CopyAgentReportVersionRoute } from "@/app/api/analysis/agentic/report/version/copy/route";
import type { GetAgentReportVersionRoute } from "@/app/api/analysis/agentic/report/version/get/route";
import type { ListAgentReportVersionsRoute } from "@/app/api/analysis/agentic/report/version/list/route";
import type { RestoreAgentReportVersionRoute } from "@/app/api/analysis/agentic/report/version/restore/route";
import type { RerunAgenticAnalysisRoute } from "@/app/api/analysis/agentic/rerun/route";
import type { GetCanvasCatalogRoute } from "@/app/api/analysis/canvas-catalog/route";
import type { ChangeThemesRoute } from "@/app/api/analysis/change_themes/route";
import type { SaveEvalCaseRoute } from "@/app/api/analysis/chat/v2/eval/save-case/route";
import type {
  ChatFeedbackRoute,
  DeleteChatFeedbackRoute,
} from "@/app/api/analysis/chat/v2/feedback/route";
import type { ChatFileDownloadRoute } from "@/app/api/analysis/chat/v2/file/download/route";
import type { LoadAnalysisChatRoute } from "@/app/api/analysis/chat/v2/load/route";
import type { Route as TurnMessageRoute } from "@/app/api/analysis/chat/v2/turn-message/route";
import type { WorkspaceChatSignedUrlRoute } from "@/app/api/analysis/chat/v2/upload/org-signed-url/route";
import type { AttachWorkspaceTemplateRoute } from "@/app/api/analysis/chat/v2/upload/workspace-template/route";
import type { CreateAspectRoute } from "@/app/api/analysis/create-aspect/route";
import type { CrosstabImageRoute } from "@/app/api/analysis/crosstab/image/route";
import type { CrosstabRoute } from "@/app/api/analysis/crosstab/route";
import type { ExportAnalysisSheetRoute } from "@/app/api/analysis/export-analysis-sheet/route";
import type { ExportChartRoute } from "@/app/api/analysis/export-chart/route";
import type { ArtifactVideoUrlRoute } from "@/app/api/analysis/export/[artifact_id]/video-url/route";
import type { AddExternalDataRoute } from "@/app/api/analysis/external-data/route";
import type { FetchLabelingDataRoute } from "@/app/api/analysis/fetch-labeling-data/route";
import type { GaborGrangerStatsRoute } from "@/app/api/analysis/gabor-granger-stats/route";
import type { MatrixStatsRoute } from "@/app/api/analysis/matrix-stats/route";
import type { MaxDiffAnalyticsRoute } from "@/app/api/analysis/max-diff-analytics/route";
import type { MaxDiffStatsRoute } from "@/app/api/analysis/max-diff-stats/route";
import type { GetPresentationDownloadUrlRoute } from "@/app/api/analysis/presentation/get_download_url/route";
import type { HidePresentationRoute } from "@/app/api/analysis/presentation/hide/route";
import type { PollPresentationRoute } from "@/app/api/analysis/presentation/poll/route";
import type { QuestionEmotionMomentsRoute } from "@/app/api/analysis/question-result/emotion-moments/route";
import type { QuestionEmotionalAnalysisRoute } from "@/app/api/analysis/question-result/emotional-analysis/route";
import type { EmotionsOverviewRoute } from "@/app/api/analysis/question-result/emotions-overview/route";
import type { QuestionChartImageRoute } from "@/app/api/analysis/question-result/export-image/route";
import type { FileUploadQuestionResultRoute } from "@/app/api/analysis/question-result/file-uploads/route";
import type { FilteredQuestionAnalysisRoute } from "@/app/api/analysis/question-result/filtered-analysis/route";
import type { GenerateQuestionAnalysesRoute } from "@/app/api/analysis/question-result/generate/route";
import type { QuestionAnalysesGenerationStatusRoute } from "@/app/api/analysis/question-result/generation-status/route";
import type { QuestionMaxDiffAnalyticsRoute } from "@/app/api/analysis/question-result/max-diff-analytics/route";
import type { QuestionResponsesRoute } from "@/app/api/analysis/question-result/responses/route";
import type { QuestionResultRoute } from "@/app/api/analysis/question-result/route";
import type { AnalysisSegmentGroupsRoute } from "@/app/api/analysis/question-result/segment-groups/route";
import type { SegmentedQuestionResultRoute } from "@/app/api/analysis/question-result/segmented/route";
import type { AnalysisQuotesRoute } from "@/app/api/analysis/quotes/route";
import type { RankingStatsRoute } from "@/app/api/analysis/ranking-stats/route";
import type { GetReelRespondentFieldsRoute } from "@/app/api/analysis/reel-respondent-fields/get";
import type { SetReelRespondentFieldsRoute } from "@/app/api/analysis/reel-respondent-fields/route";
import type { AnalysisReelsRoute } from "@/app/api/analysis/reels/route";
import type { RemoveAspectVersionRoute } from "@/app/api/analysis/remove-aspect-version/route";
import type { RemoveAspectRoute } from "@/app/api/analysis/remove-aspect/route";
import type { RemoveChartRoute } from "@/app/api/analysis/remove-chart/route";
import type { RemapAspectVersionRoute } from "@/app/api/analysis/report/remap-aspect-version/route";
import type { SaveAspectVersionRoute } from "@/app/api/analysis/save-aspect-version/route";
import type { SegmentSummariesRoute } from "@/app/api/analysis/segment-summaries/route";
import type { ShareRoute } from "@/app/api/analysis/share/route";
import type { GetAnalysisStateRoute } from "@/app/api/analysis/state/route";
import type { ResolveStudyMediaRoute } from "@/app/api/analysis/study-media/resolve/route";
import type { GetStudyMediaRoute } from "@/app/api/analysis/study-media/route";
import type { SubmitLabelingDataRoute } from "@/app/api/analysis/submit-labeling-data/route";
import type { SyncWithNewRevisionRoute } from "@/app/api/analysis/sync_with_new_revision/route";
import type { TopBoxDirectionRoute } from "@/app/api/analysis/top-box-direction/route";
import type { StudyWavesRoute } from "@/app/api/analysis/waves/route";
import type { CollectResponseRatingRoute } from "@/app/api/answer/rating/helpers";
import type { BatchChartDataRoute } from "@/app/api/artifact/chartData/route";
import type { ChangePasswordRoute } from "@/app/api/auth/change-password/route";
import type { PasswordLoginRoute } from "@/app/api/auth/password-login/route";
import type { RecoverPasswordRoute } from "@/app/api/auth/recover-password/route";
import type { SetPasswordSignupRoute } from "@/app/api/auth/set-password-signup/route";
import type { SignupEmailRoute } from "@/app/api/auth/signup-email/route";
import type { GetBillingOverviewRoute } from "@/app/api/billing/overview/route";
import type { FileUploadDescriptionRoute } from "@/app/api/chat/file-upload-description/route";
import type { InitializeTtsCartesiaRoute } from "@/app/api/chat/initialize-tts-cartesia/route";
import type { InitializeTTSElevenLabsRoute } from "@/app/api/chat/initialize-tts-elevenlabs/route";
import type { UploadAudioRoute } from "@/app/api/chat/upload-audio/route";
import type { UploadVideoRoute } from "@/app/api/chat/upload-video/route";
import type { PreviewServicePingRoute } from "@/app/api/clips/ping/route";
import type { GetVideoResponsesRoute } from "@/app/api/clips/video-responses/route";
import type { CollectAudioRoute } from "@/app/api/collect/audio/helpers";
import type { CollectRoute } from "@/app/api/collect/helpers";
import type { CollectVideoRoute } from "@/app/api/collect/video/helpers";
import type { AddContactsRoute } from "@/app/api/contact-upload/route";
import type { CreateConceptsFromFilesRoute } from "@/app/api/create/concepts-from-files/route";
import type { ParseDocRoute } from "@/app/api/create/parse-doc/route";
import type { CreateRecommendedPromptsRoute } from "@/app/api/create/recommended-prompts/route";
import type { CreateStudyRoute } from "@/app/api/create/study/route";
import type { TrackNewCreateMessagesRoute } from "@/app/api/create/track/route";
import type { CreateSheetRoute } from "@/app/api/createSheet/route";
import type { CreateConversationRoute } from "@/app/api/creation-chat/create-conversation/route";
import type { FlagCreationChatMessageRoute } from "@/app/api/creation-chat/flag-message/route";
import type { ListConversationsRoute } from "@/app/api/creation-chat/list-conversations/route";
import type { LoadConversationMessagesRoute } from "@/app/api/creation-chat/load-messages/route";
import type { CreditsRequestRoute } from "@/app/api/credits/request/route";
import type { DemoBookedCheckWebhookRoute } from "@/app/api/demo-booked/check/webhook/route";
import type { DemoBookedWebhookRoute } from "@/app/api/demo-booked/webhook/route";
import type { DemoPageVisitedRoute } from "@/app/api/demo-page-visited/route";
import type { CreateApiKeyRoute } from "@/app/api/developer/api-keys/create";
import type { DeleteApiKeyRoute } from "@/app/api/developer/api-keys/delete";
import type { GetApiKeysRoute } from "@/app/api/developer/api-keys/get";
import type { ConjointItemsWithResponsesRoute } from "@/app/api/edit-chat/conjoint-items-with-responses/route";
import type { GaborGrangerItemsWithResponsesRoute } from "@/app/api/edit-chat/gabor-granger-items-with-responses/route";
import type { MaxDiffItemsWithResponsesRoute } from "@/app/api/edit-chat/max-diff-items-with-responses/route";
import type {
  CreateEmailAccountRoute,
  DeleteEmailAccountRoute,
  GetEmailAccountsRoute,
} from "@/app/api/email/account/unified/route";
import type {
  CreateDomainEmailRoute,
  GetAllDomainEmailsRoute,
} from "@/app/api/email/domain-emails/route";
import type {
  CreateDomainRoute,
  GetAllDomainsRoute,
} from "@/app/api/email/domains/route";
import type { VerifyDomainRoute } from "@/app/api/email/domains/verify/route";
import type { DeleteEmailDataRoute } from "@/app/api/email/email-data/delete/route";
import type { GetEmailDataAllRoute } from "@/app/api/email/email-data/get-all/route";
import type { GetEmailDataByIdRoute } from "@/app/api/email/email-data/get-by-id/route";
import type { UpsertEmailDataRoute } from "@/app/api/email/email-data/route";
import type { GetEmailMetricsRoute } from "@/app/api/email/metrics/route";
import type { GetParticipantsRoute } from "@/app/api/email/participants/get/route";
import type { GetSentEmailParticipantsRoute } from "@/app/api/email/participants/get/sent-email/route";
import type { SendEmailRoute } from "@/app/api/email/send/route";
import type { CreateServerRoute } from "@/app/api/email/server/route";
import type { ResendSignatureConfirmationRoute } from "@/app/api/email/signatures/resend/route";
import type {
  CreateEmailSignatureRoute,
  GetEmailSignaturesRoute,
} from "@/app/api/email/signatures/route";
import type { EmbedCheckRoute } from "@/app/api/embed-check/route";
import type { FeatureFlagBootstrapRoute } from "@/app/api/feature-flags/bootstrap/route";
import type { SubmitFormSubmissionRoute } from "@/app/api/form-submission/route";
import type { GoogleSheetAccessRoute } from "@/app/api/google-sheet/access/route";
import type { ShareGoogleSheetRoute } from "@/app/api/google-sheet/share/route";
import type { CancelStudyImportRoute } from "@/app/api/import/study/v2/cancel/route";
import type { DenyStudyImportRoute } from "@/app/api/import/study/v2/deny/route";
import type { ImportStudyV2ExtractRoute } from "@/app/api/import/study/v2/extract/route";
import type { ExtractedStudyRoute } from "@/app/api/import/study/v2/extracted-study/route";
import type { ImportExtractedRoute } from "@/app/api/import/study/v2/import-extracted/route";
import type { ListStudyImportsRoute } from "@/app/api/import/study/v2/list/route";
import type { RetryStudyImportRoute } from "@/app/api/import/study/v2/retry/route";
import type { AbortMultipartUploadRoute } from "@/app/api/import/study/v2/upload-multipart/abort/route";
import type { CompleteMultipartUploadRoute } from "@/app/api/import/study/v2/upload-multipart/complete/route";
import type { MultipartPartUrlRoute } from "@/app/api/import/study/v2/upload-multipart/part-url/route";
import type { StartMultipartUploadRoute } from "@/app/api/import/study/v2/upload-multipart/start/route";
import type { ImportStudyV2UploadUrlRoute } from "@/app/api/import/study/v2/upload-url/route";
import type { AddIncentiveBalanceRoute } from "@/app/api/incentives/add_incentive_balance/route";
import type { GetIncentivesBalanceRoute } from "@/app/api/incentives/balance/route";
import type { CreatePendingFundingRequestRoute } from "@/app/api/incentives/create_pending_funding_request/route";
import type { ExchangeRateRoute } from "@/app/api/incentives/exchange-rate/route";
import type { PayIncentivesRoute } from "@/app/api/incentives/pay/route";
import type { ResendRewardRoute } from "@/app/api/incentives/resend_reward/route";
import type { ConsumeSlackUserLinkRequestRoute } from "@/app/api/internal/agents/slack/consume/route";
import type { MediaRelayRoute } from "@/app/api/internal/media-relay/route";
import type {
  AgreementCopiesRoute,
  AgreementDecisionRoute,
} from "@/app/api/interview/agreements/route";
import type { AgreementSourceUrlRoute } from "@/app/api/interview/agreements/source-url/route";
import type { ParticipantAssetSignedUrlRoute } from "@/app/api/interview/assets/signed-url/route";
import type { Route as ConsentRecordRoute } from "@/app/api/interview/consent-records/route";
import type { InterviewEndOfTurnRoute } from "@/app/api/interview/end-of-turn/route";
import type { PreResponseAssetSignedUrlRoute } from "@/app/api/interview/pre-response-assets/signed-url/route";
import type { PreviewSeedRoute } from "@/app/api/interview/preview/seed/route";
import type { ReportInterviewIssueRoute } from "@/app/api/interview/report-issue/route";
import type { DownloadResponseFileRoute } from "@/app/api/interview/response-file/download/route";
import type { UploadResponseFileSignedUrlRoute } from "@/app/api/interview/response-file/upload/signed-url/route";
import type { InterviewRestartRoute } from "@/app/api/interview/restart/route";
import type { SampleSurveyDataRoute } from "@/app/api/interview/sample-survey-data/route";
import type { TimeEstimationDataRoute } from "@/app/api/interview/time-estimate/route";
import type { ProcessVideoRoute } from "@/app/api/interview/video/process/route";
import type { CreateVideoUploadUrlRoute } from "@/app/api/interview/video/upload-url/route";
import type { SubmitAndWaitJobRoute } from "@/app/api/jobs/submit-and-wait/route";
import type { SubmitJobRoute } from "@/app/api/jobs/submit/route";
import type { MarketingAuthStatusRoute } from "@/app/api/marketing/auth-status/route";
import type { GetMembershipDirectoryRoute } from "@/app/api/members/directory/route";
import type { GetArtifactSurveyRoute } from "@/app/api/mission-control/artifact-survey/route";
import type {
  CreateMissionControlChatRoute,
  DeleteMissionControlChatRoute,
  GetMissionControlChatRoute,
} from "@/app/api/mission-control/chat/route";
import type { ListMissionControlChatsRoute } from "@/app/api/mission-control/chats/route";
import type { GetSurveyTitleRoute } from "@/app/api/mission-control/survey-title/route";
import type { MobileRecordingOutcomeRoute } from "@/app/api/mobile-recording/outcome/route";
import type { TranscribeLinkRoute } from "@/app/api/offline-transcribe/link/route";
import type { OnboardingRoute } from "@/app/api/onboard_user/route";
import type { OpLogRoute } from "@/app/api/op-log/route";
import type { TrackRoute } from "@/app/api/op/route";
import type { GetAvailableWorkspacesRoute } from "@/app/api/org-access/available-orgs/route";
import type { JoinOrganizationRoute } from "@/app/api/org-access/join/route";
import type { CreateAccessRequestRoute } from "@/app/api/org-access/requests/route";
import type { GetWorkspaceChartDataRoute } from "@/app/api/org-artifact/chartData/route";
import type { GetWorkspaceArtifactDataRoute } from "@/app/api/org-artifact/data/route";
import type { ExportWorkspacePresentationToDriveRoute } from "@/app/api/org-artifact/presentation/export-to-drive/route";
import type { GetWorkspacePermissionsRoute } from "@/app/api/org/permissions/route";
import type { GetOrgSettingsAccessRoute } from "@/app/api/organization/settings-access/route";
import type { GetOrganizationsRoute } from "@/app/api/organizations/route";
import type { GetOrganizationWalletsRoute } from "@/app/api/organizations/wallets/route";
import type { ApproveInvoicesRoute } from "@/app/api/outreach/panel/invoice/approve/route";
import type { GetInvoiceDraftRoute } from "@/app/api/outreach/panel/invoice/draft/route";
import type { UpdateInvoiceDraftRoute } from "@/app/api/outreach/panel/invoice/draft/update/route";
import type { ExtractInvoiceBatchRoute } from "@/app/api/outreach/panel/invoice/extract/route";
import type { ListInvoicesRoute } from "@/app/api/outreach/panel/invoice/list/route";
import type { PreviewInvoiceRoute } from "@/app/api/outreach/panel/invoice/preview/route";
import type { RejectInvoiceRoute } from "@/app/api/outreach/panel/invoice/reject/route";
import type { InvoiceUploadUrlRoute } from "@/app/api/outreach/panel/invoice/upload-url/route";
import type { IngestProlificCostReportRoute } from "@/app/api/outreach/panel/prolific-cost/ingest/route";
import type { ListProlificCostReportsRoute } from "@/app/api/outreach/panel/prolific-cost/reports/route";
import type { ProlificCostUploadUrlRoute } from "@/app/api/outreach/panel/prolific-cost/upload-url/route";
import type { IngestUserInterviewsCostReportRoute } from "@/app/api/outreach/panel/user-interviews-cost/ingest/route";
import type { ListUserInterviewsCostReportsRoute } from "@/app/api/outreach/panel/user-interviews-cost/reports/route";
import type { UserInterviewsCostUploadUrlRoute } from "@/app/api/outreach/panel/user-interviews-cost/upload-url/route";
import type { GetPageTargetsRoute } from "@/app/api/pages/targets/route";
import type { ParticipantIdentifyExistsRoute } from "@/app/api/participant/identify/exists/post";
import type { ParticipantIdentifyRoute } from "@/app/api/participant/identify/post";
import type { DeleteQuoteRoute } from "@/app/api/quotes/delete/route";
import type { InsertQuoteRoute } from "@/app/api/quotes/insert/route";
import type { UpdateQuoteRoute } from "@/app/api/quotes/update/route";
import type { ActivateLinkRoute } from "@/app/api/recruitment/activate-link/route";
import type { GetAgenticFeasibilityCheckRoute } from "@/app/api/recruitment/agentic-feasibility-check/helper";
import type {
  DeleteAudiencePlanRoute,
  GetAudiencePlansRoute,
  CreateAudiencePlanRoute,
  UpdateAudiencePlanRoute,
} from "@/app/api/recruitment/audience-plans/route";
import type { RequestAudiencePredictionsRoute } from "@/app/api/recruitment/audience-predictions/request/route";
import type { GetAudiencePredictionsRoute } from "@/app/api/recruitment/audience-predictions/route";
import type { GetAudienceStatusesRoute } from "@/app/api/recruitment/audiences/status/route";
import type { ChangeRecruitmentLinkRoute } from "@/app/api/recruitment/change-link/route";
import type { CompletionCodeRoute } from "@/app/api/recruitment/completion-code/route";
import type { CostForRecruitmentRoute } from "@/app/api/recruitment/cost/route";
import type { SetConditionalLogicRoute } from "@/app/api/recruitment/dashboard/conditional-logic/route";
import type { CreateExternalDashboardRoute } from "@/app/api/recruitment/dashboard/create-external/route";
import type { CustomLinkResponseCountsRoute } from "@/app/api/recruitment/dashboard/custom-link-response-counts/route";
import type { DeleteExternalDashboardRoute } from "@/app/api/recruitment/dashboard/delete-external/route";
import type { ExternalPanelCountsRoute } from "@/app/api/recruitment/dashboard/external-panel-counts/route";
import type { ExternalPanelListRoute } from "@/app/api/recruitment/dashboard/external-panel-list/route";
import type { ListRecruitmentDashboardsRoute } from "@/app/api/recruitment/dashboard/list/route";
import type { SetRejectReasonsExportRoute } from "@/app/api/recruitment/dashboard/reject-reasons-export/route";
import type { SaveExternalDashboardDraftRoute } from "@/app/api/recruitment/dashboard/save-external-draft/route";
import type { SendExternalEmailsRoute } from "@/app/api/recruitment/dashboard/send-external-emails/route";
import type { UpdateExternalDashboardRoute } from "@/app/api/recruitment/dashboard/update-external/route";
import type { DeleteRecruitmentRoute } from "@/app/api/recruitment/delete/route";
import type { ListRecruitmentEventsRoute } from "@/app/api/recruitment/events/[survey_id]/route";
import type { RecruitmentFiltersRoute } from "@/app/api/recruitment/filters/[panel]/route";
import type { GetNonPanelRecruitmentsRoute } from "@/app/api/recruitment/non-panel-list/route";
import type { PanelAnswerOfferRoute } from "@/app/api/recruitment/panel-api/answer-offer/route";
import type { GetPanelCountsRoute } from "@/app/api/recruitment/panel-counts/route";
import type { PauseRecruitingRoute } from "@/app/api/recruitment/pause/route";
import type { ProlificCountParticipantsRoute } from "@/app/api/recruitment/prolific/count-participants/route";
import type { ProlificWebhookRoute } from "@/app/api/recruitment/prolific/webhook/route";
import type { FetchPublicQuotasRoute } from "@/app/api/recruitment/public-quotas/route";
import type {
  QuoteDefaultsRoute,
  RequestQuoteRoute,
} from "@/app/api/recruitment/request-quote/route";
import type { RespondentCompletionUrlRoute } from "@/app/api/recruitment/respondent/completion-url/route";
import type { RespondentSearchCatalogRoute } from "@/app/api/recruitment/respondent/search-catalog/route";
import type { RespondentWebhookRoute } from "@/app/api/recruitment/respondent/webhook/route";
import type { RespondentB2BCountParticipantsRoute } from "@/app/api/recruitment/respondent_b2b/count-participants/route";
import type { RespondentB2CCountParticipantsRoute } from "@/app/api/recruitment/respondent_b2c/count-participants/route";
import type { SetupRecruitmentRoute } from "@/app/api/recruitment/setup/route";
import type { StartRecruitingRoute } from "@/app/api/recruitment/start/route";
import type { RecruitmentSyncScreenerRoute } from "@/app/api/recruitment/sync-screener/route";
import type { GetTargetGroupsRoute } from "@/app/api/recruitment/target-groups/route";
import type { UpdateTargetGroupRoute } from "@/app/api/recruitment/target-groups/update/route";
import type { TriggerRecruitmentAgentRoute } from "@/app/api/recruitment/trigger-agent/route";
import type { Route as UpdateCustomLinkRoute } from "@/app/api/recruitment/update-custom-link/route";
import type { UpdateRecruitmentAddGroupRoute } from "@/app/api/recruitment/update/add-group/route";
import type { RenameProlificProjectRoute } from "@/app/api/recruitment/update/rename/route";
import type { UpdateRecruitmentRoute } from "@/app/api/recruitment/update/route";
import type { CharacteristicRoute } from "@/app/api/recruitment/user_interviews/characteristics/route";
import type { UserInterviewsCountParticipantsRoute } from "@/app/api/recruitment/user_interviews/count-participants/route";
import type { ReelDeidentifiedDownloadRoute } from "@/app/api/reels/deidentified/download/route";
import type { ReelDeidentifiedDownloadStatusRoute } from "@/app/api/reels/deidentified/download/status/route";
import type { ReelDeidentifiedRoute } from "@/app/api/reels/deidentified/route";
import type { ReelDeidentifiedStartRoute } from "@/app/api/reels/deidentified/start/route";
import type { ReelDownloadUrlRoute } from "@/app/api/reels/download-url/route";
import type { ReelGetRoute } from "@/app/api/reels/get/route";
import type { ReelOpRoute } from "@/app/api/reels/op/route";
import type { ReelRequestEmailRoute } from "@/app/api/reels/request-email/route";
import type { CreateReelRoute, ReelsRoute } from "@/app/api/reels/route";
import type { SaveQuoteToReelRoute } from "@/app/api/reels/save-quote/route";
import type {
  GetOrCreateReelSharingRoute,
  UpdateReelSharingRoute,
} from "@/app/api/reels/sharing/route";
import type { UpdateReelSharingVariantRoute } from "@/app/api/reels/sharing/variant/route";
import type { UnlinkQuoteFromReelRoute } from "@/app/api/reels/unlink-quote/route";
import type { UpdateReelRoute } from "@/app/api/reels/update/route";
import type { ReferralSubmitRoute } from "@/app/api/referral/submit/route";
import type { ListAdminReportPromptsRoute } from "@/app/api/report-prompts/admin-list/route";
import type { ArchiveReportPromptRoute } from "@/app/api/report-prompts/archive/route";
import type { GetReportPromptRoute } from "@/app/api/report-prompts/get/route";
import type { ListReportPromptsRoute } from "@/app/api/report-prompts/list/route";
import type { SaveReportPromptRoute } from "@/app/api/report-prompts/save/route";
import type { GetReportPromptUsageRoute } from "@/app/api/report-prompts/usage/route";
import type { ListResearchLibraryAgentChatsRoute } from "@/app/api/research-library/agent-chats/route";
import type { DownloadResponsesRoute } from "@/app/api/responses/download/route";
import type { FetchHlsPlaylistContentRoute } from "@/app/api/responses/fetch-hls-playlist-content/route";
import type { FetchResponsesRoute } from "@/app/api/responses/fetch-responses/route";
import type { SingleResponseRoute } from "@/app/api/responses/fetch-single-response/route";
import type { GenerateHlsPlaylistRoute } from "@/app/api/responses/generate-hls-playlist/route";
import type { BatchResponseHistoryRoute } from "@/app/api/responses/history/route";
import type { QuestionTranscriptsRoute } from "@/app/api/responses/question-transcripts/route";
import type { GetResponseMetadataRoute } from "@/app/api/responses/response-metadata/route";
import type { SetResponseIgnoredRoute } from "@/app/api/responses/set-ignored/route";
import type { GetResponseStatsRoute } from "@/app/api/responses/stats/route";
import type { UpdateResponseTagsRoute } from "@/app/api/responses/tags/route";
import type { TranscriptExportRoute } from "@/app/api/responses/transcripts/route";
import type { UploadedFilesExportRoute } from "@/app/api/responses/uploaded-files/route";
import type { RetrieveResponsesRoute } from "@/app/api/retrieve-responses/route";
import type { RewriteRoute } from "@/app/api/rewrite/route";
import type { CustomScreenerCheckRoute } from "@/app/api/screener/custom-check/route";
import type { CheckResponseQuotasRoute } from "@/app/api/screener/quotas/route";
import type { CheckVideoQualityRoute } from "@/app/api/screener/video-quality/route";
import type { SearchResearchAgentChatsRoute } from "@/app/api/search/research-agent-chats/route";
import type { DeleteSegmentGroupRoute } from "@/app/api/segments/delete-group/route";
import type { SegmentAssignmentCountsRoute } from "@/app/api/segments/quotas/assignment-counts/route";
import type { UpdateResponseLimitRoute } from "@/app/api/segments/quotas/response_limit/update/route";
import type { UpdateQuotaRoute } from "@/app/api/segments/quotas/update/route";
import type { SaveSegmentGroupRoute } from "@/app/api/segments/save-group/route";
import type { SendEmailsRoute } from "@/app/api/send-emails/route";
import type { ChangeWorkspaceRoute } from "@/app/api/share/change-org/route";
import type { ShareListRoute } from "@/app/api/share/list/route";
import type { PreviewTokenRoute } from "@/app/api/share/preview-token/route";
import type { ShareSurveyRoute } from "@/app/api/share/survey/route";
import type { ListStudyDisclaimersRoute } from "@/app/api/studies/disclaimers/route";
import type { GetStudyRoute } from "@/app/api/studies/get/route";
import type { DeleteIssueRoute } from "@/app/api/study-checks/delete-issue/route";
import type { RequestStudyCheckRoute } from "@/app/api/study-checks/request/route";
import type { StudyCommentAuthorsRoute } from "@/app/api/study-comments/authors/route";
import type {
  GetCommentNotificationSettingRoute,
  UpdateCommentNotificationSettingRoute,
} from "@/app/api/study-comments/notification-settings/route";
import type { StudyCommentPeopleRoute } from "@/app/api/study-comments/people/route";
import type { SetIssueDismissedRoute } from "@/app/api/study-issues/set-dismissed/route";
import type { CreateStudyCommentRoute } from "@/app/api/study-versions/comment/route";
import type { CreateStudyVersionRoute } from "@/app/api/study-versions/create/route";
import type { GetConversationVersionsRoute } from "@/app/api/study-versions/get-conversation-versions/route";
import type { GetEarlierVersionsRoute } from "@/app/api/study-versions/get-earlier-versions/route";
import type { GetLatestVersionsRoute } from "@/app/api/study-versions/get-latest-versions/route";
import type { GetLatestStudyVersionRoute } from "@/app/api/study-versions/latest/route";
import type { ListStudyVersionsRoute } from "@/app/api/study-versions/list/route";
import type { StudyAssetSignedUrlRoute } from "@/app/api/survey/assets/signed-url/route";
import type { DeleteStudyRoute } from "@/app/api/survey/delete/route";
import type { SurveyDocumentUploadUrlRoute } from "@/app/api/survey/documents/upload/signed-url/route";
import type { DuplicateSurveyRoute } from "@/app/api/survey/duplicate/route";
import type { FinalizeStudyRoute } from "@/app/api/survey/finalize/route";
import type { UpdateSurveyFlagsRoute } from "@/app/api/survey/flags/route";
import type { CreateFolderRoute } from "@/app/api/survey/folder/create/route";
import type { DeleteFolderRoute } from "@/app/api/survey/folder/delete/route";
import type { MoveFolderRoute } from "@/app/api/survey/folder/move/route";
import type { UpdateFolderRoute } from "@/app/api/survey/folder/update/route";
import type { FoldersRoute } from "@/app/api/survey/folders/route";
import type { GetImportedStatusRoute } from "@/app/api/survey/imported-status/route";
import type { GetSurveyLinkIdRoute } from "@/app/api/survey/link-id/route";
import type { Route as StudyMediaUploadUrlRoute } from "@/app/api/survey/media/upload/signed-url/route";
import type { MoveSurveyRoute } from "@/app/api/survey/move/route";
import type { PauseStudyRoute } from "@/app/api/survey/pause/route";
import type { GetSurveyPermissionsRoute } from "@/app/api/survey/permissions/route";
import type { PublishRevisionRoute } from "@/app/api/survey/publish-revision/route";
import type { GetPublishedStatusRoute } from "@/app/api/survey/published-status/route";
import type { RenameStudyRoute } from "@/app/api/survey/rename/route";
import type { ReopenStudyRoute } from "@/app/api/survey/reopen/route";
import type { DeleteScreenerTemplateRoute } from "@/app/api/survey/screener-templates/delete";
import type { GetScreenerTemplatesRoute } from "@/app/api/survey/screener-templates/get";
import type { UpdateScreenerTemplateRoute } from "@/app/api/survey/screener-templates/patch";
import type { CreateScreenerTemplateRoute } from "@/app/api/survey/screener-templates/post";
import type { SearchStudiesRoute } from "@/app/api/survey/search/route";
import type { SharedSurveysWithCntAndStatusRoute } from "@/app/api/survey/shared-surveys-with-cnt-and-status/route";
import type { GetSurveyIdFromLinkRoute } from "@/app/api/survey/survey-id-from-link/route";
import type { SurveysWithCntAndStatusRoute } from "@/app/api/survey/surveys-with-cnt-and-status/route";
import type { TranslateRevisionRoute } from "@/app/api/survey/translate-revision/route";
import type { UpdateUrlParamsRoute } from "@/app/api/survey/url-params/update/route";
import type { GetSurveyWithRevisionsRoute } from "@/app/api/survey/with-revisions/route";
import type { AddTargetGroupFilterRoute } from "@/app/api/target_group/add_filter/route";
import type { TrackErrorRoute } from "@/app/api/track_error/route";
import type { TranscribeRoute } from "@/app/api/transcribe/route";
import type { TranslateSurveyRoute } from "@/app/api/translate_survey_revision/route";
import type { TTSPreviewRoute } from "@/app/api/tts/preview/route";
import type { ChatEmailSecretRoute } from "@/app/api/user/chat-email-secret/route";
import type { UpdateEmailPreferencesRoute } from "@/app/api/user/email-preferences/route";
import type { UserPatch } from "@/app/api/user/patch";
import type { GetUserRoute } from "@/app/api/user/route";
import type { PlaybackTokenRoute } from "@/app/api/video/playback-token/route";
import type { ReelRenderCompleteRoute } from "@/app/api/video/render-complete/route";
import type { EphemeralTokenRoute } from "@/app/api/video/token/route";
import type { MuxWebhookRoute } from "@/app/api/video/webhook-mux/route";
import type { SubmitTemporalWorkflowRoute } from "@/app/api/workflow/submit/route";
import type {
  GetAccessRequestsRoute,
  PatchAccessRequestRoute,
} from "@/app/api/workspace/access-requests/route";
import type { GetWorkspaceConfigRoute } from "@/app/api/workspace/config/get";
import type { UpdateWorkspaceConfig } from "@/app/api/workspace/config/update";
import type { AddOrganization } from "@/app/api/workspace/create";
import type { UpdateWorkspaceDisclaimerRoute } from "@/app/api/workspace/disclaimers/[id]/route";
import type {
  CreateWorkspaceDisclaimerRoute,
  ListWorkspaceDisclaimersRoute,
} from "@/app/api/workspace/disclaimers/route";
import type { GetOrganizationMemberAuthRoute } from "@/app/api/workspace/members/auth/route";
import type {
  AddOrganizationMember,
  DeleteOrganizationMember,
  GetOrganizationMembers,
  UpdateOrganizationMember,
} from "@/app/api/workspace/members/route";
import type { GetCustomerSlackChannelsRoute } from "@/app/api/workspace/slack/channels/route";
import type { CustomerSlackLinkRoute } from "@/app/api/workspace/slack/link/route";
import type {
  GetCustomerSlackRoute,
  UpdateCustomerSlackRoute,
} from "@/app/api/workspace/slack/route";
import type { GetCustomerSlackScheduledMessagesRoute } from "@/app/api/workspace/slack/scheduled-messages/route";
import type { SetDefaultTemplateRoute } from "@/app/api/workspace/template/default/route";
import type {
  DeleteTemplateRoute,
  FinalizeTemplateRoute,
  GetTemplateRoute,
} from "@/app/api/workspace/template/route";
import type { UploadTemplateUrlRoute } from "@/app/api/workspace/template/upload-url/route";
import type { UpdateOrganization } from "@/app/api/workspace/update";
import type { WithSLO } from "@/backend/slo";
import { clientEnv } from "@/clientEnv";
import type { RECRUITMENT_PANELS } from "@/components/outreach/panel/constants";
import { exponentialBackoffWithJitterMs, sleep } from "@/lib/utils";
import { getTraceIdFromTraceparent } from "@listen-labs/o11y/tracing";

export class RouteError extends Error {
  public statusCode: number;
  /** Machine-readable discriminator so clients can branch on a failure kind. */
  public code?: string;

  constructor(
    message: string,
    statusCode: number = 500,
    codeOrOptions?: string | { code?: string; cause?: unknown }
  ) {
    const options =
      typeof codeOrOptions === "string"
        ? { code: codeOrOptions }
        : codeOrOptions;
    const cause = options?.cause;
    super(message, notNull(cause) ? { cause } : undefined);
    this.name = "RouteError";
    this.statusCode = statusCode;
    this.code = options?.code;
  }
}

export type RouteResult<T> =
  | {
      success: false;
      error: string;
      statusCode: number;
      code?: string;
      data: undefined;
      headers?: Headers;
    }
  | {
      success: true;
      error: undefined;
      statusCode: number;
      data: T;
      headers?: Headers;
    };

export type PatchJsonRouteMapping = {
  [K in `/api/workspace/disclaimers/${string}`]: WithSLO<
    UpdateWorkspaceDisclaimerRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
} & {
  "/api/admin/credits/overrides": WithSLO<
    PatchCreditUsageOverridesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/workspace-organization": WithSLO<
    ChangeWorkspaceOrganizationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/recruitment/user_interviews/participant": WithSLO<
    UserInterviewParticipantPatchRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/recruitment/target_group": WithSLO<
    PatchTargetGroupRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/responses/quality-score": WithSLO<
    OverrideQualityScoreRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis-chat/update": WithSLO<
    UpdateAnalysisChatRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/user": WithSLO<
    UserPatch,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/user/email-preferences": WithSLO<
    UpdateEmailPreferencesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-comments/notification-settings": WithSLO<
    UpdateCommentNotificationSettingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/members": WithSLO<
    UpdateOrganizationMember,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/access-requests": WithSLO<
    PatchAccessRequestRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/workspace": WithSLO<
    UpdateOrganization,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/folder/move": WithSLO<
    MoveFolderRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/folder/update": WithSLO<
    UpdateFolderRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/flags": WithSLO<
    UpdateSurveyFlagsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/rename": WithSLO<
    RenameStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/screener-templates": WithSLO<
    UpdateScreenerTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/url-params/update": WithSLO<
    UpdateUrlParamsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/config": WithSLO<
    UpdateWorkspaceConfig,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/template/default": WithSLO<
    SetDefaultTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/update-custom-link": WithSLO<
    UpdateCustomLinkRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/target-groups/update": WithSLO<
    UpdateTargetGroupRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/audience-plans": WithSLO<
    UpdateAudiencePlanRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/update": WithSLO<
    UpdateAgentReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/set-main": WithSLO<
    SetMainReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/update": WithSLO<
    UpdateReelRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/sharing": WithSLO<
    UpdateReelSharingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/sharing/variant": WithSLO<
    UpdateReelSharingVariantRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
};

/** PostgREST migration routes. Each route file adds its own entry with `declare module`, so conversions never edit this file. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- entries come from declaration merging
export interface PostgrestMigrationRoutes {}

export type PostJsonRouteMapping = PostgrestMigrationRoutes & {
  "/api/recruitment/audience-plans": WithSLO<
    CreateAudiencePlanRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/slack": WithSLO<
    UpdateCustomerSlackRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/slack/link": WithSLO<
    CustomerSlackLinkRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/audience-predictions/request": RequestAudiencePredictionsRoute;
  "/api/pages/targets": WithSLO<
    GetPageTargetsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/agreements/version": AgreementVersionCreateRoute;
  "/api/interview/agreements": AgreementDecisionRoute;
  "/api/interview/agreements/source-url": AgreementSourceUrlRoute;
  "/api/recruitment/audience-predictions": WithSLO<
    GetAudiencePredictionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/emotion-moments": WithSLO<
    QuestionEmotionMomentsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/emotional-analysis": WithSLO<
    QuestionEmotionalAnalysisRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/emotions-overview": WithSLO<
    EmotionsOverviewRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/waves": WithSLO<
    StudyWavesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/responses": WithSLO<
    QuestionResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/file-uploads": WithSLO<
    FileUploadQuestionResultRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result": WithSLO<
    QuestionResultRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/filtered-analysis": WithSLO<
    FilteredQuestionAnalysisRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/generate": WithSLO<
    GenerateQuestionAnalysesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/generation-status": WithSLO<
    QuestionAnalysesGenerationStatusRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/max-diff-analytics": WithSLO<
    QuestionMaxDiffAnalyticsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/segment-groups": WithSLO<
    AnalysisSegmentGroupsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/segmented": WithSLO<
    SegmentedQuestionResultRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/credits/update": WithSLO<
    UpdateCreditsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/internal-supervisor-memberships": WithSLO<
    RequestInternalSupervisorMembershipRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/responses/recruited-responses": WithSLO<
    AdminUpdateRecruitedResponseRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/responses/rerun-post-processing": WithSLO<
    RerunPostProcessingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/identity/blocklist": WithSLO<
    UpdateBlocklistRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/identity/resolve": WithSLO<
    ResolveIdentityRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/webhooks/test-send": WithSLO<
    WebhookTestSendRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/panel-api/answer-offer": WithSLO<
    PanelAnswerOfferRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/render-workflows/ping": WithSLO<
    PingRenderWorkflowRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis-chat/get_themes": WithSLO<
    GetThemesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis-chat/shareable-artifacts": WithSLO<
    GetArtifactsForSurveyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/canvas-catalog": WithSLO<
    GetCanvasCatalogRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis-chat/memo/sharing": WithSLO<
    ArtifactSharingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/eval/save-case": WithSLO<
    SaveEvalCaseRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/feedback": WithSLO<
    ChatFeedbackRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/upload/workspace-template": WithSLO<
    AttachWorkspaceTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/upload/org-signed-url": WithSLO<
    WorkspaceChatSignedUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/turn-message": WithSLO<
    TurnMessageRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/add-report-item": WithSLO<
    AddReportItemRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/reel-respondent-fields": WithSLO<
    SetReelRespondentFieldsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/artifact/chartData": WithSLO<
    BatchChartDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/artifact": WithSLO<
    GetArtifactRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/analysis/study-media": WithSLO<
    GetStudyMediaRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/analysis/study-media/resolve": WithSLO<
    ResolveStudyMediaRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/analysis/agentic/data/classification/update": WithSLO<
    UpdateClassificationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/classification/recompute-status": WithSLO<
    ClassificationRecomputeStatusRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/reel": WithSLO<
    GetReelRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/report-status": WithSLO<
    GetAgenticReportStatusRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/analysis/agentic/data/state": WithSLO<
    GetAgenticAnalysisStateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/column": WithSLO<
    GetColumnsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/crosstab": CrosstabRoute;
  "/api/analysis/agentic/data/compute-chart": WithSLO<
    ComputeChartRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/static": WithSLO<
    GetStaticAnalysisDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/incentives/add_incentive_balance": WithSLO<
    AddIncentiveBalanceRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/incentives/create_pending_funding_request": WithSLO<
    CreatePendingFundingRequestRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/incentives/exchange-rate": WithSLO<
    ExchangeRateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/incentives/pay": WithSLO<
    PayIncentivesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/incentives/resend_reward": WithSLO<
    ResendRewardRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/generate": WithSLO<
    GenerateAgenticAnalysisRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/rerun": WithSLO<
    RerunAgenticAnalysisRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/presentation/details": WithSLO<
    PresentationDetailsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/presentation/export-to-drive": WithSLO<
    ExportToDriveRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/presentation/generate": WithSLO<
    GeneratePresentationV2Route,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/org-artifact/presentation/export-to-drive": WithSLO<
    ExportWorkspacePresentationToDriveRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/presentation/poll": WithSLO<
    PollPresentationV2Route,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/analysisData": WithSLO<
    LoadAnalysisDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/change_themes": WithSLO<
    ChangeThemesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/export-analysis-sheet": WithSLO<
    ExportAnalysisSheetRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/export-chart": WithSLO<
    ExportChartRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/fetch-labeling-data": WithSLO<
    FetchLabelingDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/presentation/get_download_url": WithSLO<
    GetPresentationDownloadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/presentation/hide": WithSLO<
    HidePresentationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/presentation/poll": WithSLO<
    PollPresentationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/top-box-direction": WithSLO<
    TopBoxDirectionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/matrix-stats": WithSLO<
    MatrixStatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/max-diff-stats": WithSLO<
    MaxDiffStatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/gabor-granger-stats": WithSLO<
    GaborGrangerStatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/max-diff-analytics": WithSLO<
    MaxDiffAnalyticsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/edit-chat/max-diff-items-with-responses": WithSLO<
    MaxDiffItemsWithResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/edit-chat/conjoint-items-with-responses": WithSLO<
    ConjointItemsWithResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/edit-chat/gabor-granger-items-with-responses": WithSLO<
    GaborGrangerItemsWithResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/ranking-stats": WithSLO<
    RankingStatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/segment-summaries": WithSLO<
    SegmentSummariesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/state": WithSLO<
    GetAnalysisStateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/create-aspect": WithSLO<
    CreateAspectRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/save-aspect-version": WithSLO<
    SaveAspectVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/remove-aspect": WithSLO<
    RemoveAspectRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/remove-aspect-version": WithSLO<
    RemoveAspectVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/remove-chart": WithSLO<
    RemoveChartRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/report/remap-aspect-version": WithSLO<
    RemapAspectVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/share": WithSLO<
    ShareRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/submit-labeling-data": WithSLO<
    SubmitLabelingDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/sync_with_new_revision": WithSLO<
    SyncWithNewRevisionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/answer": WithSLO<
    CollectRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/answer/audio": WithSLO<
    CollectAudioRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/answer/rating": WithSLO<
    CollectResponseRatingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/answer/video": WithSLO<
    CollectVideoRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  // Account Settings: prove the current password, rotate through admin, then
  // sign back in because the admin update invalidates every session.
  "/api/auth/change-password": WithSLO<
    ChangePasswordRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/auth/signup-email": WithSLO<
    SignupEmailRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/auth/password-login": WithSLO<
    PasswordLoginRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  // Reset-link recovery: prove a fresh recovery session and rotate through it
  // so the caller's completed MFA step-up survives.
  "/api/auth/recover-password": WithSLO<
    RecoverPasswordRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  // First password after email signup: prove a fresh email OTP session and
  // rotate through it, mirroring recovery.
  "/api/auth/set-password-signup": WithSLO<
    SetPasswordSignupRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/demo-booked/webhook": WithSLO<
    DemoBookedWebhookRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/demo-booked/check/webhook": WithSLO<
    DemoBookedCheckWebhookRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/demo-page-visited": WithSLO<
    DemoPageVisitedRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/embed-check": WithSLO<
    EmbedCheckRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/feature-flags/bootstrap": WithSLO<
    FeatureFlagBootstrapRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/form-submission": WithSLO<
    SubmitFormSubmissionRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/chat/upload-audio": WithSLO<
    UploadAudioRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/chat/file-upload-description": WithSLO<
    FileUploadDescriptionRoute,
    { target: 99; window: "30d"; group: "interview" }
  >;
  "/api/chat/upload-video": WithSLO<
    UploadVideoRoute,
    { target: 99; window: "30d"; group: "interview" }
  >;
  "/api/chat/initialize-tts-cartesia": WithSLO<
    InitializeTtsCartesiaRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/chat/initialize-tts-elevenlabs": WithSLO<
    InitializeTTSElevenLabsRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/clips/video-responses": WithSLO<
    GetVideoResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/collect": WithSLO<
    CollectRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/collect/audio": WithSLO<
    CollectAudioRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/collect/video": WithSLO<
    CollectVideoRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/contact-upload": WithSLO<
    AddContactsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/credits/request": WithSLO<
    CreditsRequestRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/create/track": WithSLO<
    TrackNewCreateMessagesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/createSheet": WithSLO<
    CreateSheetRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/google-sheet/access": WithSLO<
    GoogleSheetAccessRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/google-sheet/share": WithSLO<
    ShareGoogleSheetRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/create/study": WithSLO<
    CreateStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/creation-chat/create-conversation": WithSLO<
    CreateConversationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/creation-chat/flag-message": WithSLO<
    FlagCreationChatMessageRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/creation-chat/list-conversations": WithSLO<
    ListConversationsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/creation-chat/load-messages": WithSLO<
    LoadConversationMessagesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/mobile-recording/outcome": WithSLO<
    MobileRecordingOutcomeRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/op": WithSLO<
    TrackRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/op-log": WithSLO<
    OpLogRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/track_error": WithSLO<
    TrackErrorRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/report-prompts/save": WithSLO<
    SaveReportPromptRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/generate-hls-playlist": WithSLO<
    GenerateHlsPlaylistRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/fetch-hls-playlist-content": WithSLO<
    FetchHlsPlaylistContentRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/import/study/v2/cancel": WithSLO<
    CancelStudyImportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/deny": WithSLO<
    DenyStudyImportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/extract": WithSLO<
    ImportStudyV2ExtractRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/import-extracted": WithSLO<
    ImportExtractedRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/retry": WithSLO<
    RetryStudyImportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/upload-multipart/abort": WithSLO<
    AbortMultipartUploadRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/upload-multipart/complete": WithSLO<
    CompleteMultipartUploadRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/upload-multipart/part-url": WithSLO<
    MultipartPartUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/upload-multipart/start": WithSLO<
    StartMultipartUploadRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/upload-url": WithSLO<
    ImportStudyV2UploadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/internal/agents/slack/consume": WithSLO<
    ConsumeSlackUserLinkRequestRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/internal/media-relay": WithSLO<
    MediaRelayRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/interview/time-estimate": WithSLO<
    TimeEstimationDataRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/interview/sample-survey-data": WithSLO<
    SampleSurveyDataRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/interview/preview/seed": WithSLO<
    PreviewSeedRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/interview/restart": WithSLO<
    InterviewRestartRoute,
    { target: 99; window: "30d"; group: "interview" }
  >;
  "/api/interview/end-of-turn": WithSLO<
    InterviewEndOfTurnRoute,
    { target: 99; window: "30d"; group: "interview" }
  >;
  "/api/interview/consent-records": WithSLO<
    ConsentRecordRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/interview/report-issue": WithSLO<
    ReportInterviewIssueRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/interview/assets/signed-url": WithSLO<
    ParticipantAssetSignedUrlRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/interview/pre-response-assets/signed-url": WithSLO<
    PreResponseAssetSignedUrlRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/interview/response-file/upload/signed-url": WithSLO<
    UploadResponseFileSignedUrlRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/mission-control/chats": WithSLO<
    ListMissionControlChatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/research-library/agent-chats": WithSLO<
    ListResearchLibraryAgentChatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/mission-control/chat": WithSLO<
    CreateMissionControlChatRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/mission-control/survey-title": WithSLO<
    GetSurveyTitleRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/mission-control/artifact-survey": WithSLO<
    GetArtifactSurveyRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/jobs/submit": WithSLO<
    SubmitJobRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/jobs/submit-and-wait": WithSLO<
    SubmitAndWaitJobRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/offline-transcribe/link": WithSLO<
    TranscribeLinkRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/quotes/delete": WithSLO<
    DeleteQuoteRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/quotes/insert": WithSLO<
    InsertQuoteRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/quotes/update": WithSLO<
    UpdateQuoteRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/activate-link": WithSLO<
    ActivateLinkRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/change-link": WithSLO<
    ChangeRecruitmentLinkRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/completion-code": WithSLO<
    CompletionCodeRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/recruitment/cost": WithSLO<
    CostForRecruitmentRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/create-external": WithSLO<
    CreateExternalDashboardRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/delete-external": WithSLO<
    DeleteExternalDashboardRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/save-external-draft": WithSLO<
    SaveExternalDashboardDraftRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/conditional-logic": WithSLO<
    SetConditionalLogicRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/reject-reasons-export": WithSLO<
    SetRejectReasonsExportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/send-external-emails": WithSLO<
    SendExternalEmailsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/update-external": WithSLO<
    UpdateExternalDashboardRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/delete": WithSLO<
    DeleteRecruitmentRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/duplicate": WithSLO<
    DuplicateSurveyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/prolific/count-participants": WithSLO<
    ProlificCountParticipantsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/pause": WithSLO<
    PauseRecruitingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/prolific/webhook": WithSLO<
    ProlificWebhookRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/recruitment/respondent/webhook": WithSLO<
    RespondentWebhookRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/recruitment/respondent/completion-url": WithSLO<
    RespondentCompletionUrlRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/recruitment/request-quote": WithSLO<
    RequestQuoteRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/public-quotas": WithSLO<
    FetchPublicQuotasRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/setup": WithSLO<
    SetupRecruitmentRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/start": WithSLO<
    StartRecruitingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/trigger-agent": WithSLO<
    TriggerRecruitmentAgentRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/update": WithSLO<
    UpdateRecruitmentRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/update/add-group": WithSLO<
    UpdateRecruitmentAddGroupRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/update/rename": WithSLO<
    RenameProlificProjectRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/user_interviews/count-participants": WithSLO<
    UserInterviewsCountParticipantsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/respondent_b2c/count-participants": WithSLO<
    RespondentB2CCountParticipantsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/respondent_b2b/count-participants": WithSLO<
    RespondentB2BCountParticipantsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/sync-screener": WithSLO<
    RecruitmentSyncScreenerRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/download": WithSLO<
    DownloadResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/fetch-single-response": WithSLO<
    SingleResponseRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/response-metadata": WithSLO<
    GetResponseMetadataRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/responses/history": WithSLO<
    BatchResponseHistoryRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/fetch-responses": WithSLO<
    FetchResponsesRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/responses/set-ignored": WithSLO<
    SetResponseIgnoredRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/tags": WithSLO<
    UpdateResponseTagsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/question-transcripts": WithSLO<
    QuestionTranscriptsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/retrieve-responses": WithSLO<
    RetrieveResponsesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/rewrite": WithSLO<
    RewriteRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/screener-templates": WithSLO<
    CreateScreenerTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/screener/custom-check": WithSLO<
    CustomScreenerCheckRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/screener/quotas": WithSLO<
    CheckResponseQuotasRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/segments/delete-group": WithSLO<
    DeleteSegmentGroupRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/segments/save-group": WithSLO<
    SaveSegmentGroupRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/segments/quotas/update": WithSLO<
    UpdateQuotaRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/segments/quotas/response_limit/update": WithSLO<
    UpdateResponseLimitRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/search/research-agent-chats": WithSLO<
    SearchResearchAgentChatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workflow/submit": WithSLO<
    SubmitTemporalWorkflowRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/send-emails": WithSLO<
    SendEmailsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/share/change-org": WithSLO<
    ChangeWorkspaceRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/share/preview-token": WithSLO<
    PreviewTokenRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/share/survey": WithSLO<
    ShareSurveyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/access/restriction": WithSLO<
    SetRestrictionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/access/grant": WithSLO<
    GrantAccessRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/access/revoke": WithSLO<
    RevokeAccessRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/access/link": WithSLO<
    SetLinkSharingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-checks/delete-issue": WithSLO<
    DeleteIssueRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-checks/request": WithSLO<
    RequestStudyCheckRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-issues/set-dismissed": WithSLO<
    SetIssueDismissedRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/comment": WithSLO<
    CreateStudyCommentRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/create": WithSLO<
    CreateStudyVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/get-conversation-versions": WithSLO<
    GetConversationVersionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/get-earlier-versions": WithSLO<
    GetEarlierVersionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/get-latest-versions": WithSLO<
    GetLatestVersionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/latest": WithSLO<
    GetLatestStudyVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-versions/list": WithSLO<
    ListStudyVersionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/assets/signed-url": WithSLO<
    StudyAssetSignedUrlRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/media/upload/signed-url": WithSLO<
    StudyMediaUploadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/documents/upload/signed-url": WithSLO<
    SurveyDocumentUploadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/finalize": WithSLO<
    FinalizeStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/pause": WithSLO<
    PauseStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/reopen": WithSLO<
    ReopenStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/move": WithSLO<
    MoveSurveyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/publish-revision": WithSLO<
    PublishRevisionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/folders": WithSLO<
    FoldersRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/folder/create": WithSLO<
    CreateFolderRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/surveys-with-cnt-and-status": WithSLO<
    SurveysWithCntAndStatusRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/search": WithSLO<
    SearchStudiesRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/shared-surveys-with-cnt-and-status": WithSLO<
    SharedSurveysWithCntAndStatusRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/target_group/add_filter": WithSLO<
    AddTargetGroupFilterRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/transcribe": WithSLO<
    TranscribeRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/translate-revision": WithSLO<
    TranslateRevisionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/translate_survey_revision": WithSLO<
    TranslateSurveyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/tts/preview": WithSLO<
    TTSPreviewRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/interview/video/process": WithSLO<
    ProcessVideoRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/interview/video/upload-url": WithSLO<
    CreateVideoUploadUrlRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/video/token": WithSLO<
    EphemeralTokenRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/video/webhook-mux": WithSLO<
    MuxWebhookRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/video/playback-token": WithSLO<
    PlaybackTokenRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/video/render-complete": WithSLO<
    ReelRenderCompleteRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/reels/deidentified": WithSLO<
    ReelDeidentifiedRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/deidentified/download": WithSLO<
    ReelDeidentifiedDownloadRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/deidentified/download/status": WithSLO<
    ReelDeidentifiedDownloadStatusRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/deidentified/start": WithSLO<
    ReelDeidentifiedStartRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/download-url": WithSLO<
    ReelDownloadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/get": WithSLO<
    ReelGetRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/op": WithSLO<
    ReelOpRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/request-email": WithSLO<
    ReelRequestEmailRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels": WithSLO<
    CreateReelRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/sharing": WithSLO<
    GetOrCreateReelSharingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/save-quote": WithSLO<
    SaveQuoteToReelRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels/unlink-quote": WithSLO<
    UnlinkQuoteFromReelRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace": WithSLO<
    AddOrganization,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/disclaimers": WithSLO<
    CreateWorkspaceDisclaimerRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/members": WithSLO<
    AddOrganizationMember,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/developer/api-keys": WithSLO<
    CreateApiKeyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/template": WithSLO<
    FinalizeTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/template/upload-url": WithSLO<
    UploadTemplateUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/prolific-cost/upload-url": WithSLO<
    ProlificCostUploadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/prolific-cost/ingest": WithSLO<
    IngestProlificCostReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/upload-url": WithSLO<
    InvoiceUploadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/extract": WithSLO<
    ExtractInvoiceBatchRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/draft/update": WithSLO<
    UpdateInvoiceDraftRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/reject": WithSLO<
    RejectInvoiceRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/preview": WithSLO<
    PreviewInvoiceRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/approve": WithSLO<
    ApproveInvoicesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/user-interviews-cost/upload-url": WithSLO<
    UserInterviewsCostUploadUrlRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/user-interviews-cost/ingest": WithSLO<
    IngestUserInterviewsCostReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/onboard_user": WithSLO<
    OnboardingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/org-access/join": WithSLO<
    JoinOrganizationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/org-access/requests": WithSLO<
    CreateAccessRequestRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/participant/identify": WithSLO<
    ParticipantIdentifyRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/participant/identify/exists": WithSLO<
    ParticipantIdentifyExistsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/email/server": WithSLO<
    CreateServerRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/domains": WithSLO<
    CreateDomainRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/domains/verify": WithSLO<
    VerifyDomainRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/domain-emails": WithSLO<
    CreateDomainEmailRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/signatures": WithSLO<
    CreateEmailSignatureRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/signatures/resend": WithSLO<
    ResendSignatureConfirmationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/email-data": WithSLO<
    UpsertEmailDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/email-data/delete": WithSLO<
    DeleteEmailDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/email-data/get-all": WithSLO<
    GetEmailDataAllRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/email-data/get-by-id": WithSLO<
    GetEmailDataByIdRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/participants/get": WithSLO<
    GetParticipantsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/participants/get/sent-email": WithSLO<
    GetSentEmailParticipantsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/send": WithSLO<
    SendEmailRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/account/unified": WithSLO<
    CreateEmailAccountRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/metrics": WithSLO<
    GetEmailMetricsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/referral/submit": WithSLO<
    ReferralSubmitRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/list": WithSLO<
    ListAgentReportsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/analysis/agentic/report/create": WithSLO<
    CreateAgentReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/duplicate": WithSLO<
    DuplicateAgentReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/edit/upload/create-artifact": WithSLO<
    CreateReportFileArtifactRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/generate": WithSLO<
    GenerateAgentReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/generate/in-progress": WithSLO<
    ListInProgressReportGenerationRunsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/version/list": WithSLO<
    ListAgentReportVersionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/version/get": WithSLO<
    GetAgentReportVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/version/copy": WithSLO<
    CopyAgentReportVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/version/restore": WithSLO<
    RestoreAgentReportVersionRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
} & {
  [K in `/api/admin/orgs/${string}/webhook-portal`]: WithSLO<
    WebhookPortalRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
};

export type PostFormRouteMapping = {
  "/api/analysis/external-data": WithSLO<
    AddExternalDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/create/concepts-from-files": WithSLO<
    CreateConceptsFromFilesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/create/parse-doc": WithSLO<
    ParseDocRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/screener/video-quality": WithSLO<
    CheckVideoQualityRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
};

export type PostBlobRouteMapping = {
  "/api/analysis/crosstab/image": WithSLO<
    CrosstabImageRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/transcripts": WithSLO<
    TranscriptExportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/uploaded-files": WithSLO<
    UploadedFilesExportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/export-preview-chart-image": WithSLO<
    ExportPreviewChartImageRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/question-result/export-image": WithSLO<
    QuestionChartImageRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
};

export type GetRouteMapping = {
  "/api/recruitment/request-quote": WithSLO<
    QuoteDefaultsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/reel-respondent-fields": WithSLO<
    GetReelRespondentFieldsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/slack/channels": WithSLO<
    GetCustomerSlackChannelsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/slack/scheduled-messages": WithSLO<
    GetCustomerSlackScheduledMessagesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/slack": WithSLO<
    GetCustomerSlackRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/interview/agreements": AgreementCopiesRoute;
  [path: `/api/agreements/version/${string}`]: AgreementVersionReadRoute;
  "/api/outreach/panel/invoice/list": WithSLO<
    ListInvoicesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/invoice/draft": WithSLO<
    GetInvoiceDraftRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/prolific-cost/reports": WithSLO<
    ListProlificCostReportsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/outreach/panel/user-interviews-cost/reports": WithSLO<
    ListUserInterviewsCostReportsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/marketing/auth-status": WithSLO<
    MarketingAuthStatusRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/user": WithSLO<
    GetUserRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/user/chat-email-secret": WithSLO<
    ChatEmailSecretRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/admin/billing-entities": WithSLO<
    GetBillingEntitiesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/billing-entities/wallets": WithSLO<
    GetWalletsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/workspace-organization": WithSLO<
    GetWorkspaceOrganizationRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/credits/overrides": WithSLO<
    GetCreditUsageOverrideRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/recruitment/user_interviews/participant": WithSLO<
    UserInterviewParticipantGetRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/studies": WithSLO<
    GetStudiesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/internal-supervisor-memberships": WithSLO<
    ListWorkspacesWithoutSupervisorAccessRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/admin/internal-supervisor-memberships/org-surveys": WithSLO<
    ListWorkspaceSurveysRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis-chat/get_chats": WithSLO<
    GetAnalysisChatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/load": WithSLO<
    LoadAnalysisChatRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/data/segment-definitions": WithSLO<
    GetSegmentDefinitionsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/org-artifact/chartData": WithSLO<
    GetWorkspaceChartDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/org-artifact/data": WithSLO<
    GetWorkspaceArtifactDataRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/clips/ping": WithSLO<
    PreviewServicePingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/reels": WithSLO<
    AnalysisReelsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/quotes": WithSLO<
    AnalysisQuotesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/reels": WithSLO<
    ReelsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/org-access/available-orgs": WithSLO<
    GetAvailableWorkspacesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/members/directory": WithSLO<
    GetMembershipDirectoryRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/organization/settings-access": WithSLO<
    GetOrgSettingsAccessRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/studies/get": WithSLO<
    GetStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/studies/disclaimers": WithSLO<
    ListStudyDisclaimersRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/members": WithSLO<
    GetOrganizationMembers,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/members/auth": WithSLO<
    GetOrganizationMemberAuthRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/developer/api-keys": WithSLO<
    GetApiKeysRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/agentic-feasibility-check": WithSLO<
    GetAgenticFeasibilityCheckRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/access-requests": WithSLO<
    GetAccessRequestsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/workspace/config": WithSLO<
    GetWorkspaceConfigRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/user_interviews/characteristics": WithSLO<
    CharacteristicRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/respondent/search-catalog": WithSLO<
    RespondentSearchCatalogRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/organizations": WithSLO<
    GetOrganizationsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/organizations/wallets": WithSLO<
    GetOrganizationWalletsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/domains": WithSLO<
    GetAllDomainsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/domain-emails": WithSLO<
    GetAllDomainEmailsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/signatures": WithSLO<
    GetEmailSignaturesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/account/unified": WithSLO<
    GetEmailAccountsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/metrics": WithSLO<
    GetEmailMetricsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/mission-control/chat": WithSLO<
    GetMissionControlChatRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/link-id": WithSLO<
    GetSurveyLinkIdRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/recruitment/non-panel-list": WithSLO<
    GetNonPanelRecruitmentsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/panel-counts": WithSLO<
    GetPanelCountsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/target-groups": WithSLO<
    GetTargetGroupsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/audiences/status": WithSLO<
    GetAudienceStatusesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/audience-plans": WithSLO<
    GetAudiencePlansRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/survey-id-from-link": WithSLO<
    GetSurveyIdFromLinkRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/permissions": WithSLO<
    GetSurveyPermissionsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/org/permissions": WithSLO<
    GetWorkspacePermissionsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/screener-templates": WithSLO<
    GetScreenerTemplatesRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/share/list": WithSLO<
    ShareListRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/access/list": WithSLO<
    ListAccessRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-comments/authors": WithSLO<
    StudyCommentAuthorsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-comments/people": WithSLO<
    StudyCommentPeopleRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/study-comments/notification-settings": WithSLO<
    GetCommentNotificationSettingRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/file/download": WithSLO<
    ChatFileDownloadRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/interview/response-file/download": WithSLO<
    DownloadResponseFileRoute,
    { target: 99.9; window: "30d"; group: "interview" }
  >;
  "/api/create/recommended-prompts": WithSLO<
    CreateRecommendedPromptsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/import/study/v2/extracted-study": WithSLO<
    ExtractedStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/import/study/v2/list": WithSLO<
    ListStudyImportsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/imported-status": WithSLO<
    GetImportedStatusRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/published-status": WithSLO<
    GetPublishedStatusRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/survey/with-revisions": WithSLO<
    GetSurveyWithRevisionsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
  "/api/report-prompts/list": WithSLO<
    ListReportPromptsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/report-prompts/admin-list": WithSLO<
    ListAdminReportPromptsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/report-prompts/get": WithSLO<
    GetReportPromptRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/report-prompts/usage": WithSLO<
    GetReportPromptUsageRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/incentives/balance": WithSLO<
    GetIncentivesBalanceRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/billing/overview": WithSLO<
    GetBillingOverviewRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/template": WithSLO<
    GetTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/disclaimers": WithSLO<
    ListWorkspaceDisclaimersRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/custom-link-response-counts": WithSLO<
    CustomLinkResponseCountsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/external-panel-counts": WithSLO<
    ExternalPanelCountsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/external-panel-list": WithSLO<
    ExternalPanelListRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/recruitment/dashboard/list": WithSLO<
    ListRecruitmentDashboardsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/responses/stats": WithSLO<
    GetResponseStatsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/segments/quotas/assignment-counts": WithSLO<
    SegmentAssignmentCountsRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
} & {
  [K in `/api/recruitment/filters/${(typeof RECRUITMENT_PANELS)[number]}`]: WithSLO<
    RecruitmentFiltersRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
} & {
  [K in `/api/recruitment/events/${string}`]: WithSLO<
    ListRecruitmentEventsRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
} & {
  [K in `/api/analysis/export/${string}/video-url`]: WithSLO<
    ArtifactVideoUrlRoute,
    { target: 99; window: "30d"; group: "generic" }
  >;
};

export type DeleteJsonRouteMapping = {
  "/api/recruitment/audience-plans": WithSLO<
    DeleteAudiencePlanRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/report-prompts/archive": WithSLO<
    ArchiveReportPromptRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/chat/v2/feedback": WithSLO<
    DeleteChatFeedbackRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis-chat/update": WithSLO<
    DeleteAnalysisChatRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/delete": WithSLO<
    DeleteStudyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/folder/delete": WithSLO<
    DeleteFolderRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/members": WithSLO<
    DeleteOrganizationMember,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/developer/api-keys": WithSLO<
    DeleteApiKeyRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/email/account/unified": WithSLO<
    DeleteEmailAccountRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/mission-control/chat": WithSLO<
    DeleteMissionControlChatRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/workspace/template": WithSLO<
    DeleteTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/survey/screener-templates": WithSLO<
    DeleteScreenerTemplateRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
  "/api/analysis/agentic/report/delete": WithSLO<
    DeleteAgentReportRoute,
    { target: 99; window: "30d"; group: "low-traffic" }
  >;
};

// Every request carries a fresh `traceparent` (the server echoes its trace ID
// as `x-request-id`) and the browser scope's IDs as W3C `baggage`.
export const fetchWithTraceHeaders = async (
  input: RequestInfo | URL,
  init: RequestInit,
  {
    onTraceId,
    shouldPinDeployment = true,
  }: {
    onTraceId?: (traceId: string | undefined) => void;
    // False sends the request to the current build instead of the one this tab loaded.
    shouldPinDeployment?: boolean;
  } = {}
): Promise<Response> => {
  const pendingSessionCheck = waitForFreshApiSession();
  if (pendingSessionCheck) {
    await pendingSessionCheck;
  }
  const headers = new Headers(init.headers);
  Object.entries(getTraceHeaders()).forEach(([name, value]) =>
    headers.set(name, value)
  );
  const deploymentId = clientEnv.NEXT_DEPLOYMENT_ID;
  if (shouldPinDeployment && typeof window !== "undefined" && deploymentId) {
    const requestUrl = new URL(
      input instanceof Request ? input.url : input,
      window.location.href
    );
    if (requestUrl.origin === window.location.origin) {
      headers.set("x-deployment-id", deploymentId);
    }
  }
  onTraceId?.(getTraceIdFromTraceparent(headers.get("traceparent")));
  return fetch(input, { ...init, headers });
};

let isPageUnloading = false;
const UNLOAD_CANCEL_GRACE_MS = 1000;
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    isPageUnloading = true;
    setTimeout(() => {
      isPageUnloading = false;
    }, UNLOAD_CANCEL_GRACE_MS);
  });
  window.addEventListener("pagehide", () => {
    isPageUnloading = true;
  });
  window.addEventListener("pageshow", () => {
    isPageUnloading = false;
  });
}

const submitSafe = async <T>(
  config: { retries?: number; signal?: AbortSignal },
  fn: () => Promise<Response>,
  attempt = 0
): Promise<RouteResult<T>> => {
  const retries = config.retries ?? 0;
  try {
    const response = await fn();
    try {
      const body = (await response.json()) as Record<string, unknown>;
      return {
        ...body,
        success: body.success ?? response.ok,
        statusCode: body.statusCode ?? response.status,
        headers: response.headers,
      } as RouteResult<T>;
    } catch (error) {
      if (retries === 0) {
        console.error("Error in submitSafe:", error);
        return {
          success: false,
          error: response.statusText,
          statusCode: response.status,
          data: undefined,
          headers: response.headers,
        };
      } else {
        throw error;
      }
    }
  } catch (error) {
    const isAborted = () => config.signal?.aborted || isPageUnloading;
    const abortedResult: RouteResult<T> = {
      success: false,
      error: "Request was aborted",
      statusCode: 499,
      data: undefined,
    };
    if (isAborted()) return abortedResult;

    console.error("Network error in submitSafe:", error);
    if (retries > 0) {
      console.warn("Retrying in submitSafe. Retries left:", retries);
      // An immediate retry fails again during a short connection drop.
      await sleep(
        exponentialBackoffWithJitterMs({
          attempt,
          initialDelayMs: 500,
          maxBaseDelayMs: 4000,
          backoffMultiplier: 3,
        })
      );
      if (isAborted()) return abortedResult;
      return submitSafe({ ...config, retries: retries - 1 }, fn, attempt + 1);
    }
    console.error("No more retries left in submitSafe:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      statusCode: 503,
      data: undefined,
    };
  }
};

const submitBlobSafe = async (
  config: { retries?: number; signal?: AbortSignal },
  fn: () => Promise<Response>
): Promise<
  | { success: true; blob: Blob; filename?: string }
  | { success: false; error: string; statusCode: number }
> => {
  const retries = config.retries ?? 0;
  try {
    const response = await fn();

    if (response.ok) {
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = contentDisposition
        ? contentDisposition.split('filename="')[1]?.split('"')[0]
        : undefined;

      return {
        success: true,
        blob,
        filename,
      };
    } else {
      // Try to parse error response as JSON
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const errorResult = await response.json();
        return {
          success: false,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          error: errorResult.error || "Unknown error",
          statusCode: response.status,
        };
      } catch {
        return {
          success: false,
          error: "Unknown error",
          statusCode: response.status,
        };
      }
    }
  } catch (error) {
    // Check if aborted
    if (config.signal?.aborted || isPageUnloading) {
      return {
        success: false,
        error: "Request was aborted",
        statusCode: 499,
      };
    }

    console.error("Network error in submitBlobSafe:", error);
    if (retries > 0) {
      console.warn("Retrying in submitBlobSafe. Retries left:", retries);
      return submitBlobSafe({ ...config, retries: retries - 1 }, fn);
    }
    console.error("No more retries left in submitBlobSafe:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      statusCode: 503,
    };
  }
};

export const getJson = async <
  T extends keyof GetRouteMapping,
  U extends RemoveExcessProperties<GetRouteMapping[T]["input"], U>,
>(
  url: T,
  params: U,
  config: {
    base_url?: `${"http" | "https"}://${string}`;
    retries?: number;
    signal?: AbortSignal;
  } = {}
): Promise<RouteResult<GetRouteMapping[T]["output"]>> =>
  submitSafe<GetRouteMapping[T]["output"]>(config, async () => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value);
      }
    });
    const queryString = searchParams.toString();
    const endpoint = notNull(config.base_url) ? config.base_url + url : url;
    const fullUrl = queryString ? `${endpoint}?${queryString}` : endpoint;
    return await fetchWithTraceHeaders(fullUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      signal: config.signal,
    });
  });

export const postJson = async <
  T extends keyof PostJsonRouteMapping,
  U extends RemoveExcessProperties<PostJsonRouteMapping[T]["input"], U>,
>(
  url: T,
  data: U,
  config: {
    skipLogs?: boolean;
    retries?: number;
    signal?: AbortSignal;
    shouldPinDeployment?: boolean;
  } = {}
): Promise<RouteResult<PostJsonRouteMapping[T]["output"]>> =>
  submitSafe(config, async () => {
    const body = JSON.stringify(data);

    // Auto-sign if route is in SIGNED_ROUTES
    const signatureHeaders = isSignedRoute(url)
      ? generateSignatureHeaders(body)
      : null;

    if (!config.skipLogs) {
      console.log("posting,", url, data);
    }
    return await fetchWithTraceHeaders(
      url,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...signatureHeaders,
        },
        body,
        signal: config.signal,
      },
      { shouldPinDeployment: config.shouldPinDeployment }
    );
  });

export const patchJson = async <
  T extends keyof PatchJsonRouteMapping,
  U extends RemoveExcessProperties<PatchJsonRouteMapping[T]["input"], U>,
>(
  url: T,
  data: U,
  config: { skipLogs?: boolean; retries?: number; signal?: AbortSignal } = {}
): Promise<RouteResult<PatchJsonRouteMapping[T]["output"]>> =>
  submitSafe(config, async () => {
    if (!config.skipLogs) {
      console.log("patching,", url, data);
    }
    return await fetchWithTraceHeaders(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      signal: config.signal,
    });
  });

export const deleteJson = async <
  T extends keyof DeleteJsonRouteMapping,
  U extends RemoveExcessProperties<DeleteJsonRouteMapping[T]["input"], U>,
>(
  url: T,
  data: U,
  config: { retries?: number; signal?: AbortSignal } = {}
): Promise<RouteResult<DeleteJsonRouteMapping[T]["output"]>> =>
  submitSafe(config, async () => {
    return await fetchWithTraceHeaders(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      signal: config.signal,
    });
  });

export const postForm = async <
  T extends keyof PostFormRouteMapping,
  U extends RemoveExcessProperties<PostFormRouteMapping[T]["input"], U>,
>(
  url: T,
  data: U,
  config: { retries?: number; signal?: AbortSignal } = {}
): Promise<RouteResult<PostFormRouteMapping[T]["output"]>> =>
  submitSafe(config, async () => {
    const formData = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v instanceof File || v instanceof Blob) {
        formData.append(k, v);
      } else if (
        Array.isArray(v) &&
        (v as unknown[]).every(
          (item) => item instanceof File || item instanceof Blob
        )
      ) {
        v.forEach((file) => formData.append(k, file));
      } else {
        formData.append(k, JSON.stringify(v));
      }
    });
    console.log("posting:", formData);
    return await fetchWithTraceHeaders(url, {
      method: "POST",
      body: formData,
      signal: config.signal,
    });
  });

export const postBlob = async <
  T extends keyof PostBlobRouteMapping,
  U extends RemoveExcessProperties<PostBlobRouteMapping[T]["input"], U>,
>(
  url: T,
  data: U,
  config: { skipLogs?: boolean; retries?: number; signal?: AbortSignal } = {}
): Promise<
  | { success: true; blob: Blob; filename?: string; contentType?: string }
  | { success: false; error: string; statusCode: number }
> =>
  submitBlobSafe(config, async () => {
    if (!config.skipLogs) {
      console.log("posting for blob,", url, data);
    }
    return await fetchWithTraceHeaders(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
      signal: config.signal,
    });
  });
